import { createResource, createSignal, For, Show, onMount } from "solid-js";
import { A, useNavigate, useSearchParams } from "@solidjs/router";
import { useApi, isOk } from "../api";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "../components/MessageBox";
import Text from "../components/Text";
import Modal from "../components/Modal";
import ItemIdentifierPicker from "../components/ItemIdentifierPicker";
import ItemIdentityWizard from "../components/ItemIdentityWizard";
import { ahListingWizardSource, toStack } from "../services/ahListing";
import { Badge } from "../components/Badge";
import { EmptyState } from "../components/EmptyState";
import { SkeletonAuditRow } from "../components/Skeleton";
import ItemStackView from "../components/ItemStackView";
import { useCraftingResolver } from "../services/craftingResolverContext";
import { fromIdentifier } from "../services/itemRefAdapters";
import { formatInteger } from "../services/numberFormat";
import { formatDateTime } from "../services/dateFormat";
import { resolveItemDisplayName } from "../services/itemDisplayName";
import type { AhWatchDto, AhWatchRequest, SellbotWizardRequest } from "../api/generated/schemas";
import type { ItemStack } from "../models/itemStack";
import styles from "./AhWatches.module.css";

const DEFAULT_LEADS = [60, 10];

function blankWatch(name: string, itemId: number, metadata: number, snbt: string | null): AhWatchRequest {
  return {
    name,
    isEnabled: true,
    itemId,
    metadata,
    snbt,
    maxPrice: null,
    binOnly: false,
    notifyOnNewMatch: true,
    notifyBeforeExpiry: true,
    notifyOnOutbid: true,
    notifyOnPriceBelowCeiling: false,
    leadMinutes: [...DEFAULT_LEADS],
    rules: [],
  };
}

export default function AhWatches() {
  const api = useApi();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const resolver = useCraftingResolver();
  const [searchParams, setSearchParams] = useSearchParams();

  const watchName = (stack: Pick<ItemStack, "itemId" | "damage" | "snbt" | "name">) =>
    resolveItemDisplayName(stack, resolver()) || t("AH_WATCH_UNNAMED");

  const [pickerOpen, setPickerOpen] = createSignal(false);
  const [wizardOpen, setWizardOpen] = createSignal(false);
  const [creating, setCreating] = createSignal(false);

  const [watches] = createResource(async () => {
    const res = await api.ahWatch.list();
    if (!isOk(res.data)) return [] as AhWatchDto[];
    return res.data.value!.items;
  });

  const create = async (request: AhWatchRequest) => {
    setCreating(true);
    try {
      const res = await api.ahWatch.create(request);
      if (!isOk(res.data) || !res.data.value) {
        toast?.error(t("AH_WATCH_SAVE_FAILED"));
        return;
      }
      // Straight into the watch's own page, the way a new sellbot offer opens its detail page.
      navigate(`/ah/watches/${res.data.value.id}`);
    } finally {
      setCreating(false);
    }
  };

  // "Watch this" on a listing carries the sample over, so the new watch starts from that exact item.
  onMount(async () => {
    const fromListing = searchParams.fromListing;
    if (typeof fromListing !== "string" || !fromListing) return;

    setSearchParams({ fromListing: undefined });
    const res = await api.ah.getListing(fromListing);
    if (!isOk(res.data) || !res.data.value) return;

    const listing = res.data.value;
    await create(blankWatch(
      watchName(toStack(listing)),
      listing.itemId ?? 0,
      listing.itemDamage ?? 0,
      listing.snbt ?? null,
    ));
  });

  const wizardSource = ahListingWizardSource(api, "AH_WATCH_PICK_ITEM");

  const wizardDiff = async (request: SellbotWizardRequest) => {
    const res = await api.ahWatch.wizardDiff(request);
    return isOk(res.data)
      ? { ok: true as const, value: res.data.value! }
      : { ok: false as const, error: t("AH_WATCH_SAVE_FAILED") };
  };

  const wizardFinish = async (request: SellbotWizardRequest) => {
    const first = request.items[0];
    setCreating(true);
    try {
      const created = await api.ahWatch.create(blankWatch(
        watchName({ itemId: first.itemId, damage: first.metadata, snbt: undefined }),
        first.itemId, first.metadata, null));
      if (!isOk(created.data) || !created.data.value) return { ok: false, error: t("AH_WATCH_SAVE_FAILED") };
      const applied = await api.ahWatch.applyRules(created.data.value.id, request);
      if (!isOk(applied.data)) return { ok: false, error: t("AH_WATCH_SAVE_FAILED") };
      navigate(`/ah/watches/${created.data.value.id}`);
      return { ok: true };
    } finally {
      setCreating(false);
    }
  };

  const createFromIdentifier = async (identifier: string) => {
    const stack = fromIdentifier(identifier, 0, resolver());
    if (!stack.itemId) {
      toast?.error(t("AH_WATCH_NEEDS_ITEM"));
      return;
    }
    setPickerOpen(false);
    await create(blankWatch(stack.name ?? identifier, stack.itemId, stack.damage ?? 0, null));
  };

  return (
    <div class={styles.container}>
      <div class={styles.headerRow}>
        <div class={styles.titleBlock}>
          <h1><Text val="AH_WATCHES_TITLE" /></h1>
          <p class={styles.subtitle}><Text val="AH_WATCHES_SUBTITLE" /></p>
        </div>
        <div class={styles.headerActions}>
          <A href="/ah" class="btn btn-secondary">
            <i class="fa-solid fa-gavel" /> <Text val="AH_NAV_BROWSE" />
          </A>
          <button class="btn btn-secondary" disabled={creating()} onClick={() => setWizardOpen(true)}>
            <i class="fa-solid fa-wand-magic-sparkles" /> <Text val="AH_WATCHES_WIZARD_START" />
          </button>
          <button class="btn btn-primary" disabled={creating()} onClick={() => setPickerOpen(true)}>
            <i class="fa-solid fa-plus" /> <Text val="AH_WATCH_NEW" />
          </button>
        </div>
      </div>

      <ItemIdentityWizard open={wizardOpen()} onClose={() => setWizardOpen(false)}
        source={wizardSource} diff={wizardDiff} onFinish={wizardFinish} />

      <Show
        when={watches.state !== "pending"}
        fallback={<div class={styles.grid}><For each={Array(3).fill(0)}>{() => <SkeletonAuditRow />}</For></div>}
      >
        <Show
          when={(watches()?.length ?? 0) > 0}
          fallback={
            <EmptyState
              illustration="jobs"
              title={t("AH_WATCHES_EMPTY_TITLE")}
              hint={t("AH_WATCHES_EMPTY_HINT")}
              action={<button class="btn btn-primary" onClick={() => setPickerOpen(true)}><Text val="AH_WATCH_NEW" /></button>}
            />
          }
        >
          <div class={styles.grid}>
            <For each={watches()!}>{(watch) => <WatchCard watch={watch} />}</For>
          </div>
        </Show>
      </Show>

      <Modal open={pickerOpen()} onClose={() => setPickerOpen(false)} title={t("AH_WATCH_NEW_TITLE")}>
        <div class="v" style={{ gap: "var(--spacing-md)" }}>
          <p class={styles.subtitle}><Text val="AH_WATCH_PICK_ITEM_HINT" /></p>
          <ItemIdentifierPicker
            value={[]}
            single
            metaAware
            placeholder={t("AH_WATCH_PICK_ITEM")}
            onChange={(next) => { if (next.length > 0) void createFromIdentifier(next[0]); }}
          />
        </div>
      </Modal>
    </div>
  );
}

function WatchCard(props: { watch: AhWatchDto }) {
  return (
    <A href={`/ah/watches/${props.watch.id}`} class={`fcard ${styles.card}`}>
      <div class={styles.cardHead}>
        <div class={styles.cardTitle}>
          <ItemStackView
            stack={{ itemId: props.watch.itemId, count: 1, damage: props.watch.metadata, snbt: props.watch.snbt ?? undefined }}
            size={32}
          />
          <span class={styles.cardName}>{props.watch.name}</span>
          <Badge tone={props.watch.isEnabled ? "success" : "neutral"} pill>
            <Text val={props.watch.isEnabled ? "AH_WATCH_ENABLED" : "AH_WATCH_DISABLED"} />
          </Badge>
        </div>
        <i class={`fa-solid fa-chevron-right ${styles.cardChevron}`} />
      </div>

      <div class={styles.cardStats}>
        <div class={styles.cardStat}>
          <span class={styles.cardStatLabel}><Text val="AH_WATCH_MATCHES" /></span>
          <span class={styles.cardStatValue}>{formatInteger(props.watch.activeMatchCount)}</span>
        </div>
        <div class={styles.cardStat}>
          <span class={styles.cardStatLabel}><Text val="AH_WATCH_NEXT_EXPIRY" /></span>
          <span class={styles.cardStatValueSm}>
            {props.watch.nextExpiryUtc ? formatDateTime(props.watch.nextExpiryUtc) : "--"}
          </span>
        </div>
        <div class={styles.cardStat}>
          <span class={styles.cardStatLabel}><Text val="AH_WATCH_RULES" /></span>
          <span class={styles.cardStatValueSm}>{formatInteger(props.watch.rules?.length ?? 0)}</span>
        </div>
      </div>

      <div class={styles.cardTags}>
        <Show when={props.watch.notifyOnNewMatch}><Badge tone="info" pill><Text val="AH_WATCH_NOTIFY_NEW" /></Badge></Show>
        <Show when={props.watch.notifyBeforeExpiry}>
          <Badge tone="info" pill>
            <Text val="AH_WATCH_NOTIFY_EXPIRY" /> {(props.watch.leadMinutes ?? []).map((lead: number) => `${lead}m`).join(", ")}
          </Badge>
        </Show>
        <Show when={props.watch.notifyOnOutbid}><Badge tone="info" pill><Text val="AH_WATCH_NOTIFY_OUTBID" /></Badge></Show>
        <Show when={props.watch.notifyOnPriceBelowCeiling}><Badge tone="info" pill><Text val="AH_WATCH_NOTIFY_CEILING" /></Badge></Show>
      </div>
    </A>
  );
}
